import {
  deserify,
  Loader,
  TypographyHtml,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Button, Checkbox, Typography } from '@mui/material';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useRef, useState } from 'react';
import { useAppSelector } from '../../reduxStore/reduxHooks';
import {
  ContentElementType,
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import PrintableQuestionPaper from '../Assessment/QuestionPaperGenerator/ReviewQuestionPaper/PrintableQuestionPaper';
import {
  QuestionSectionMeta,
  SectionQuestionMetaData,
} from '@protos/learning_management/lms.db_pb';
import { questionHasImage } from '../Assessment/QuestionPaperGenerator/ReviewQuestionPaper/utils';

const DemoFormativeQPG = () => {
  const [assessmentId, setAssessmentId] = useState('');
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const [questionList, setQuestionList] = useState<Question[]>([]);
  const [title, setTitle] = useState('');
  const [printAnswer, setPrintAnswer] = useState(false);
  const [showImages, setShowImages] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [fontSize, setFontSize] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const printablePaperRef = useRef<any>(null);

  const { LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const renderFormativeAssessment = async (formativeId: string) => {
    try {
      if (!user_info?.teacherProfileId || !formativeId) {
        return;
      }
      setSubmitted(true);
      setLoading(true);
      const activeHomeworkContent =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: user_info?.teacherProfileId,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: (formativeId ? BigInt(formativeId) : undefined),
          }
        );
      const taskQuestionInfo =
        activeHomeworkContent?.data?.questionsSequenceInfo?.taskQuestionInfo ||
        [];
      const sortedQuestionIds = taskQuestionInfo
        .sort((a, b) => a.sequence - b.sequence)
        .map((val) => val.questionId)
        .filter((val) => val !== undefined) as string[];
      const questions = activeHomeworkContent?.data?.assessmentContent || [];
      const sortedQuestions = sortedQuestionIds
        .map((id) => questions.find((elem) => elem.questionId === id))
        .filter((elem) => elem !== undefined) as Question[];
      const showImagesCopy = { ...showImages };
      for (let i = 0; i < sortedQuestions.length; i++) {
        if (
          sortedQuestions[i].questionType ===
          Question_QuestionEnum.QUESTION_TYPE_TF
        ) {
          if (
            sortedQuestions[i].question?.model.value?.commonQuestionContent
              ?.elements
          ) {
            sortedQuestions[
              i
            ].question?.model.value?.commonQuestionContent?.elements.push(
              new ContentElementType({
                model: {
                  case: 'text',
                  value: {
                    text: '(A - True / B - False)',
                  },
                },
              })
            );
          }
        }
        const quesId = sortedQuestions[i]?.questionId;
        if (questionHasImage(sortedQuestions[i]) && quesId) {
          showImagesCopy[quesId] = true;
        }
      }
      setShowImages(showImagesCopy);
      setQuestionList(sortedQuestions);
    } catch (err) {
      setQuestionList([]);
      setSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        padding: '20px',
        pointerEvents: loading ? 'none' : 'auto',
        opacity: loading ? 0.5 : 1,
      }}
    >
      {!submitted ? (
        <>
          Formative Assessment Id:
          <Box
            component={'input'}
            type="number"
            value={assessmentId}
            onChange={(ev) => setAssessmentId(ev.target.value)}
            sx={{
              width: '100%',
              marginBottom: '10px',
            }}
          />
          Title:
          <Box
            component={'input'}
            type="text"
            value={title}
            onChange={(ev) => setTitle(ev.target.value)}
            sx={{
              width: '100%',
              marginBottom: '10px',
            }}
          />
          Printable Font Size:
          <Box
            component={'input'}
            type="number"
            value={fontSize}
            onChange={(ev) => setFontSize(ev.target.value)}
            sx={{
              width: '100%',
              marginBottom: '10px',
            }}
          />
          {/* <Box sx={{ color: 'red', mY: '10px' }}>{errorMsg}</Box> */}
          <br />
          <br />
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              renderFormativeAssessment(assessmentId);
            }}
          >
            Submit
          </Button>
        </>
      ) : (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            setSubmitted(false);
          }}
        >
          Go Back
        </Button>
      )}
      {submitted && questionList.length > 0 && (
        <Box sx={{ mt: '20px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setPrintAnswer(false);
                setTimeout(async () => {
                  await printablePaperRef.current?.handlePrint(false);
                }, 1000);
              }}
            >
              Question Paper
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setPrintAnswer(true);
                setTimeout(async () => {
                  await printablePaperRef.current?.handlePrint(true);
                }, 1000);
              }}
            >
              Answer Paper
            </Button>
          </Box>
          <Box
            sx={{
              marginTop: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
            }}
          >
            {questionList.map((ques, index) => {
              const quesId = ques.questionId;
              if (!quesId) {
                return null;
              }
              return (
                <Box
                  key={ques.questionId || index}
                  sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}
                  >
                    <Box>Q {index + 1}.</Box>
                    <TypographyHtml>{ques.questionText}</TypographyHtml>
                    {questionHasImage(ques) && (
                      <Checkbox
                        checked={showImages[quesId]}
                        onChange={(event) =>
                          setShowImages({
                            ...showImages,
                            [quesId]: event.target.checked,
                          })
                        }
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Box>
            <PrintableQuestionPaper
              questionSectionMetaInfo={[
                new QuestionSectionMeta({
                  sectionQuestionsInfo: questionList.map(
                    (ques, index) =>
                      new SectionQuestionMetaData({
                        questionId: ques.questionId,
                        sequence: index,
                        showMedia: ques?.questionId
                          ? !!showImages[ques.questionId]
                          : true,
                      })
                  ),
                }),
              ]}
              sectionMetaStatements={[]}
              questionsList={questionList}
              printAnswer={printAnswer}
              ref={printablePaperRef}
              instructions={[]}
              loading={loading}
              setLoading={setLoading}
              printableFontSize={
                !isNaN(Number(fontSize)) ? Number(fontSize) : undefined
              }
              // schoolName={user_info?.schoolDetails[0]?.schoolName}
              assessmentTitle={title}
              // grade={qpgAssessmentTask?.class}
              // section={qpgAssessmentTask?.section}
              // subject={qpgAssessmentTask?.subject}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DemoFormativeQPG;
